import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NgModel } from '@angular/forms';

import { LifecycleHooks } from '@app/core/services/lifecycle-hooks.service';

@Component({
  standalone: false,
  selector: 'adspt-input-tag',
  templateUrl: 'input-tag.component.html',
  styleUrls: ['input-tag.component.scss'],
  providers: [LifecycleHooks],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputTagComponent {
  protected readonly document = document;

  currentValue?: string;

  @Input()
  placeholder?: string;

  @Input()
  tags?: string[];

  @Input()
  delimiters: string[] = [',', '\n', ';'];

  @Output()
  readonly tagsUpdated = new EventEmitter<string[]>();

  @Output()
  readonly enterPressed = new EventEmitter<string[]>();

  @ViewChild(NgModel, { static: true })
  ngModel?: NgModel;

  @ViewChild('input', { static: true, read: ElementRef })
  input?: ElementRef<HTMLInputElement>;

  onEnter(event: UIEvent): void {
    event.stopImmediatePropagation();
    event.preventDefault();

    this.assertAddTag();
    this.enterPressed.emit(this.tags);
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    event.stopImmediatePropagation();

    const pastedText = event.clipboardData?.getData('text/plain');

    if (pastedText) {
      this.assertAddTag(pastedText);
      this.updateTags();
    }
  }

  onTagClose(event: MouseEvent, tag: string): void {
    event.stopPropagation();
    this.assertRemoveTag(tag);
  }

  assertAddTag(value = this.currentValue): void {
    const newTags =
      value
        ?.trim()
        .split(new RegExp(`[${this.delimiters.join('')}]`))
        .map((tag) => tag.trim())
        .filter((tag) => !!tag) || [];

    if (newTags.length) {
      this.addTags(newTags);
      this.updateTags();
    }
  }

  assertRemoveTag(tag: string): void {
    if (this.tags.includes(tag)) {
      this.removeTag(tag);
      this.updateTags();
      this.input.nativeElement.focus();
    }
  }

  private addTags(tags: string[]): void {
    this.tags ||= [];

    tags.forEach((tag) => {
      if (this.tags.includes(tag)) {
        this.removeTag(tag);
      }
    });

    this.tags = [...this.tags, ...tags];
    this.ngModel.control.reset();
  }

  private removeTag(remove: string): void {
    this.tags = this.tags.filter((tag) => tag !== remove);
  }

  private updateTags(): void {
    this.tagsUpdated.next(this.tags);
  }
}
